// src/hooks/useMetaPixel.ts
import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getFbp, getFbc } from "@/lib/meta-cookies";

const generateEventId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const hashValue = async (value: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(value.trim().toLowerCase());
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

interface UserDataOverrides {
  email?: string;
  phone?: string;
  externalId?: string;
}

type EventName = "PageView" | "Lead" | "InitiateCheckout" | "Purchase" | "CompleteRegistration";

/**
 * Read _fbp with a retry — the Meta Pixel SDK sets this cookie async,
 * so on first page load it may not exist yet.
 */
const getFbpWithRetry = async (): Promise<string> => {
  let fbp = getFbp();
  if (!fbp) {
    await new Promise((r) => setTimeout(r, 1000));
    fbp = getFbp();
  }
  return fbp;
};

export const useMetaPixel = () => {
  const getAuthUserData = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return {};
    return {
      email: session.user.email,
      externalId: session.user.id,
    };
  }, []);

  const trackEvent = useCallback(
    async (
      eventName: EventName,
      userDataOverrides?: UserDataOverrides,
      customData?: Record<string, unknown>
    ) => {
      const eventId = generateEventId();
      const fbc = getFbc();
      const fbp = await getFbpWithRetry();

      // Auto-enrich from auth context
      const authData = await getAuthUserData();

      // Merge: explicit overrides > auth data
      const email = userDataOverrides?.email || authData.email;
      const externalId = userDataOverrides?.externalId || authData.externalId;

      // 1. Client-side Pixel
      if (window.fbq) {
        window.fbq("track", eventName, customData || {}, { eventID: eventId });
      }

      // 2. Server-side CAPI only for conversion events (PageView handled by Pixel)
      if (eventName === "PageView") return;

      try {
        await supabase.functions.invoke("meta-capi", {
          body: {
            event_name: eventName,
            event_id: eventId,
            event_source_url: window.location.href,
            user_data: {
              email,
              phone: userDataOverrides?.phone,
              external_id: externalId,
              fbc,
              fbp,
              client_user_agent: navigator.userAgent,
            },
            custom_data: customData,
          },
        });
      } catch (err) {
        console.warn("CAPI event failed:", err);
      }
    },
    [getAuthUserData]
  );

  const trackPageView = useCallback(
    (userData?: UserDataOverrides) => trackEvent("PageView", userData),
    [trackEvent]
  );

  const trackLead = useCallback(
    (userData?: UserDataOverrides) => trackEvent("Lead", userData),
    [trackEvent]
  );

  const trackInitiateCheckout = useCallback(
    (userData?: UserDataOverrides, value?: number) =>
      trackEvent("InitiateCheckout", userData, value ? { value, currency: "BRL" } : undefined),
    [trackEvent]
  );

  const trackPurchase = useCallback(
    (userData?: UserDataOverrides, value?: number) =>
      trackEvent("Purchase", userData, value ? { value, currency: "BRL" } : undefined),
    [trackEvent]
  );

  const trackCompleteRegistration = useCallback(
    (userData?: UserDataOverrides) => trackEvent("CompleteRegistration", userData),
    [trackEvent]
  );

  return {
    trackEvent,
    trackPageView,
    trackLead,
    trackInitiateCheckout,
    trackPurchase,
    trackCompleteRegistration,
    hashValue,
  };
};
